import Cookies from "js-cookie";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Bounce, toast, ToastContainer, ToastOptions } from "react-toastify";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import {
    ChronosLicense,
    getAllowedCU,
    getCUUsage,
    hasValidLicense,
    LicenseCUUsageData,
} from "userful-chronos-app-common-js/dist/models/license/license";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import { useLicenseSelector } from "userful-chronos-common-store/dist/license";
import LicenseAlertToast from "./LicenseAlertToast";
import LicenseCUAlertToast from "./LicenseCUAlertToast";

export const LICENSE_ALERT_ID = "licenseAlert";
export const LICENSE_CU_CONTAINER_ID = "licenseCUAlert";
const LICENSE_CU_USAGE_ID = "licenseCUUsage";

const toastOptions: ToastOptions = {
    position: "top-right",
    autoClose: false,
    className: "notificationTostify",
    containerId: LICENSE_ALERT_ID,
    toastId: LICENSE_ALERT_ID,
};

const cuToastOptions: ToastOptions = {
    position: "bottom-right",
    autoClose: false,
    className: "notificationTostify",
    containerId: LICENSE_ALERT_ID,
    toastId: LICENSE_CU_USAGE_ID,
};

const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;
const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;

const FIRST_LICENSE_THRESHOLD_MS = ONE_MONTH * 6; // 6 months
const SECOND_LICENSE_THRESHOLD_MS = ONE_MONTH * 2; // 2 months
const THIRD_LICENSE_THRESHOLD_MS = ONE_MONTH; // 1 month

const LICENSE_CU_THRESHOLD = 0.85; // 85% of CU usage

const shouldShowCUAlert = (): boolean => {
    return !Cookies.get(`userful-notification-${LICENSE_CU_CONTAINER_ID}`);
};

const isAdmin = (permission: UserPermission): boolean =>
    permission.isAdminUser ||
    permission.isDecisionsAdmin ||
    permission.isEngageAdmin ||
    permission.isRubyAdmin ||
    permission.isSystemConfigAdmin ||
    permission.isUserManagementAdmin ||
    permission.isTabletCtrlAdmin ||
    permission.isSapphireAdmin;

const isSuperAdmin = (permissions: UserPermission): boolean => permissions.isAdminUser;

const getWarningExpry = (permission: UserPermission, license: ChronosLicense, ready: boolean): number => {
    if (!ready) {
        return -1;
    }
    if (!hasValidLicense(license)) {
        return -1;
    }
    if (license.allowPerpetualMode) {
        return -1;
    }
    const expiry = license.expiryDate;
    const now = Date.now();
    const timeleft = expiry - now;
    const previousShownTimeMS = Number(Cookies.get(`${LICENSE_ALERT_ID}`)) || 0;

    if (timeleft < THIRD_LICENSE_THRESHOLD_MS) {
        // if license has 1 months left, show alert to all admin at 1 week interval
        if (isAdmin(permission) && now - previousShownTimeMS >= ONE_WEEK) {
            return expiry;
        } else {
            return -1;
        }
    }

    if (timeleft < SECOND_LICENSE_THRESHOLD_MS) {
        // if license has 2 months left, show alert to super admin at 1 week interval
        if (isSuperAdmin(permission) && now - previousShownTimeMS >= ONE_WEEK) {
            return expiry;
        } else {
            return -1;
        }
    }

    if (timeleft < FIRST_LICENSE_THRESHOLD_MS) {
        // if license has 2 months left, show alert to super admin at 1 month interval
        if (isSuperAdmin(permission) && now - previousShownTimeMS >= ONE_MONTH) {
            return expiry;
        } else {
            return -1;
        }
    }
    return -1;
};

const shouldShowError = (license: ChronosLicense, ready: boolean): boolean => {
    if (!ready) {
        return false;
    }
    return !hasValidLicense(license);
};

const shouldShowCUError = (licenseCU: LicenseCUUsageData, license: ChronosLicense, ready: boolean): boolean => {
    return false;
    if (!ready) {
        return false;
    }
    if (!hasValidLicense(license)) {
        return false;
    }
    if (getAllowedCU(licenseCU, license) < 1) {
        return false;
    }
    return getCUUsage(licenseCU, license) > getAllowedCU(licenseCU, license);
};

const shouldShowCUWarning = (licenseCU: LicenseCUUsageData, license: ChronosLicense, ready: boolean): boolean => {
    if (!ready) {
        return false;
    }
    if (!hasValidLicense(license)) {
        return false;
    }
    if (license.licenseTier.cu < 1) {
        return false;
    }
    return getCUUsage(licenseCU, license) > getAllowedCU(licenseCU, license) * LICENSE_CU_THRESHOLD;
};

const LicenseAlertContainer = () => {
    const { t } = useTranslation();
    const userPermission = getGlobalStates().userPermission;
    const license = useLicenseSelector((s) => s.licenseStore.license);
    const licenseCU = useLicenseSelector((s) => s.licenseStore.licenseCUUsage);
    const ready = useLicenseSelector((s) => s.licenseStore.ready);

    const dismissToast = (licenseError: boolean) => {
        if (!licenseError) {
            Cookies.set(`${LICENSE_ALERT_ID}`, `${Date.now()}`, { expires: 30 });
        }
        toast.dismiss(LICENSE_ALERT_ID);
    };

    useEffect(() => {
        const licenseError = shouldShowError(license, ready);
        const licenseWarningExpiry = getWarningExpry(userPermission, license, ready);

        if (licenseError) {
            toast.warn(
                <LicenseAlertToast
                    content={t("ConfigApp.LicenseDetails.licenseExpiryError")}
                    hideToast={() => dismissToast(licenseError)}
                />,
                toastOptions,
            );
        } else if (licenseWarningExpiry > 0) {
            toast.warn(
                <LicenseAlertToast
                    content={t("ConfigApp.LicenseDetails.licenseExpiryWarning", {
                        date: new Date(licenseWarningExpiry).toLocaleDateString(),
                    })}
                    hideToast={() => dismissToast(licenseError)}
                />,
                toastOptions,
            );
        }
        if (isAdmin(userPermission) && shouldShowCUAlert()) {
            const licenseCUError = shouldShowCUError(licenseCU, license, ready);
            const licenseCUWarning = shouldShowCUWarning(licenseCU, license, ready);
            if (licenseCUError) {
                toast.error(
                    <LicenseCUAlertToast
                        content={t("ConfigApp.LicenseDetails.licenseCUError")}
                        hideToast={() => toast.dismiss(LICENSE_CU_USAGE_ID)}
                    />,
                    { ...cuToastOptions },
                );
            } else if (licenseCUWarning) {
                console.debug("Showing CU warning toast");
                toast.warn(
                    <LicenseCUAlertToast
                        content={t("ConfigApp.LicenseDetails.licenseCUWarning")}
                        hideToast={() => toast.dismiss(LICENSE_CU_USAGE_ID)}
                    />,
                    { ...cuToastOptions },
                );
            }
        } else {
            console.debug("CU alert dismissed by user");
        }
    }, [license, licenseCU, ready]);

    return <ToastContainer transition={Bounce} containerId={LICENSE_ALERT_ID} className="customContainer" />;
};

export default LicenseAlertContainer;
